import { useState, useMemo, useCallback } from "react";
import { cn } from "../lib/utils";
import { Badge } from "../components/ui/Badge";
import { EmptyState } from "../components/ui/EmptyState";
import { Sheet, SheetHeader, SheetBody } from "../components/ui/Sheet";
import { useContacts } from "../hooks/useContacts";
import type { ContactStatus } from "../data/contacts-data";
import type { Id } from "../../convex/_generated/dataModel";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Search01Icon,
  UserMultiple02Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  Mail01Icon,
  Calendar03Icon,
  Tick01Icon,
} from "@hugeicons/core-free-icons";

// Convex contact type (from DB)
interface ConvexContact {
  _id: Id<"contacts">;
  _creationTime: number;
  name: string;
  email: string;
  source: "waitlist" | "twitter" | "linkedin" | "manual";
  status: ContactStatus;
  purpose: string;
  notes: string;
  dateAdded: string;
  platform: string;
}

// ── Shared styles ───────────────────────────────────────────
const selectCls = cn(
  "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
  "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
  "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
  "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
  "hover:border-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)]",
);

const thCls = cn(
  "h-[36px] px-[var(--spacing-4)] text-left text-[11px] font-semibold uppercase tracking-[0.06em]",
  "text-[var(--color-text-muted)] border-b border-[var(--color-border-stroke)]",
  "select-none whitespace-nowrap bg-[var(--color-bg-sidebar)]",
);

const tdCls = cn(
  "px-[var(--spacing-4)] py-[var(--spacing-2)] text-[14px] text-[var(--color-text-primary)]",
  "border-b border-[var(--color-border-stroke)]",
);

type SortKey = "name" | "email" | "dateAdded" | "status" | "source";
type SortDir = "asc" | "desc";
type ContactSegment = "waitlist" | "warm" | "alesso" | "partners";

function getContactSegment(contact: ConvexContact): ContactSegment {
  const notes = contact.notes.toLowerCase();
  const purpose = contact.purpose.toLowerCase();
  const name = contact.name.toLowerCase();

  if (contact.source === "waitlist") return "waitlist";
  if (name.includes("alesso") || notes.includes("5000")) return "alesso";
  if (purpose.includes("warm") || notes.includes("intro") || notes.includes("contact list")) return "warm";
  return "partners";
}

function parseNextAction(notes: string): string | null {
  const parts = notes.split("|").map((chunk) => chunk.trim());
  for (const part of parts) {
    if (!part.toLowerCase().startsWith("next_action=")) continue;
    return part.slice("next_action=".length).trim();
  }
  return null;
}

export function ContactsPage() {
  const { contacts, isLoading, updateContact } = useContacts();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ContactStatus | "all">("all");
  const [sourceFilter, setSourceFilter] = useState<ConvexContact["source"] | "all">("all");
  const [segmentFilter, setSegmentFilter] = useState<ContactSegment | "all">("all");
  const [sortKey, setSortKey] = useState<SortKey>("dateAdded");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // ── Sheet state ───────────────────────────────────────────
  const [selectedContactId, setSelectedContactId] = useState<Id<"contacts"> | null>(null);
  const selectedContact = useMemo(
    () => (contacts as ConvexContact[]).find((c) => c._id === selectedContactId) ?? null,
    [contacts, selectedContactId],
  );

  const typedContacts = contacts as ConvexContact[];

  // ── Derived ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = typedContacts;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.purpose.toLowerCase().includes(q) ||
          c.notes.toLowerCase().includes(q),
      );
    }
    if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
    if (sourceFilter !== "all") list = list.filter((c) => c.source === sourceFilter);
    if (segmentFilter !== "all") list = list.filter((c) => getContactSegment(c) === segmentFilter);
    return list;
  }, [typedContacts, search, statusFilter, sourceFilter, segmentFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const statusCounts = useMemo(() => {
    const counts: Record<ContactStatus | "total", number> = {
      to_contact: 0,
      contacted: 0,
      connected: 0,
      not_relevant: 0,
      total: typedContacts.length,
    };
    for (const c of typedContacts) counts[c.status]++;
    return counts;
  }, [typedContacts]);

  // ── Handlers ──────────────────────────────────────────────
  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey],
  );

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return null;
    return (
      <HugeiconsIcon
        icon={sortDir === "asc" ? ArrowUp01Icon : ArrowDown01Icon}
        size={12}
        className="ml-[2px] inline-block opacity-70"
      />
    );
  };

  const hasFilters = search !== "" || statusFilter !== "all" || sourceFilter !== "all" || segmentFilter !== "all";

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-[14px] text-[var(--color-text-muted)]">Loading contacts…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="shrink-0 px-[var(--spacing-4)] pt-[var(--spacing-4)] pb-[var(--spacing-3)] border-b border-[var(--color-border-stroke)]">
        <div className="flex items-center justify-between mb-[var(--spacing-3)]">
          <div>
            <h1 className="text-[20px] font-semibold text-[var(--color-text-primary)] leading-[28px]">
              Contacts
            </h1>
            <p className="text-[14px] text-[var(--color-text-muted)] mt-[var(--spacing-1)]">
              {statusCounts.total} unique contacts from waitlist &amp; outreach
            </p>
          </div>
        </div>

        {/* KPI chips */}
        <div className="flex items-center gap-[var(--spacing-3)] mb-[var(--spacing-3)]">
          <KpiChip label="Total" value={statusCounts.total} icon={UserMultiple02Icon} />
          <KpiChip label="To Contact" value={statusCounts.to_contact} icon={Mail01Icon} variant="warning" />
          <KpiChip label="Contacted" value={statusCounts.contacted} icon={Calendar03Icon} variant="info" />
          <KpiChip label="Connected" value={statusCounts.connected} icon={Tick01Icon} variant="success" />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-[var(--spacing-2)] flex-wrap">
          <div className="relative">
            <HugeiconsIcon
              icon={Search01Icon}
              size={16}
              className="absolute left-[8px] top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search contacts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={cn(
                "h-[32px] w-[240px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
                "bg-[var(--color-bg-canvas)] pl-[32px] pr-[var(--spacing-2)]",
                "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
                "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
                "hover:border-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)]",
              )}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ContactStatus | "all")}
            className={selectCls}
          >
            <option value="all">All Statuses</option>
            <option value="to_contact">To Contact</option>
            <option value="contacted">Contacted</option>
            <option value="connected">Connected</option>
            <option value="not_relevant">Not Relevant</option>
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as ConvexContact["source"] | "all")}
            className={selectCls}
          >
            <option value="all">All Sources</option>
            <option value="waitlist">Waitlist</option>
            <option value="twitter">Twitter</option>
            <option value="linkedin">LinkedIn</option>
            <option value="manual">Manual</option>
          </select>

          <select
            value={segmentFilter}
            onChange={(e) => setSegmentFilter(e.target.value as ContactSegment | "all")}
            className={selectCls}
          >
            <option value="all">All Segments</option>
            <option value="waitlist">Waitlist</option>
            <option value="warm">Warm</option>
            <option value="alesso">Alesso</option>
            <option value="partners">Partners</option>
          </select>

          {hasFilters && (
            <button
              onClick={() => { setSearch(""); setStatusFilter("all"); setSourceFilter("all"); setSegmentFilter("all"); }}
              className={cn(
                "h-[32px] px-[var(--spacing-3)] rounded-[var(--radius-md)]",
                "text-[14px] text-[var(--color-accent)] hover:bg-[var(--color-accent-subtle-bg)]",
                "transition-colors duration-[var(--duration-fast)] cursor-pointer",
              )}
            >
              Clear
            </button>
          )}

          <span className="ml-auto text-[12px] text-[var(--color-text-muted)]">
            {sorted.length} of {contacts.length} shown
          </span>
        </div>
      </div>

      {/* ── Table — edge-to-edge, no padding, no radius ──── */}
      <div className="flex-1 min-h-0 overflow-auto">
        {sorted.length === 0 ? (
          <EmptyState
            title={hasFilters ? "No contacts match filters" : "No contacts yet"}
            description={hasFilters ? "Try adjusting your search or filter criteria." : "Import your waitlist or add contacts manually."}
            variant="search"
          />
        ) : (
          <table className="w-full border-collapse text-[14px]">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className={cn(thCls, "cursor-pointer hover:text-[var(--color-text-primary)]")} onClick={() => handleSort("name")}>
                  Name <SortIcon column="name" />
                </th>
                <th className={cn(thCls, "cursor-pointer hover:text-[var(--color-text-primary)]")} onClick={() => handleSort("email")}>
                  Email <SortIcon column="email" />
                </th>
                <th className={cn(thCls, "cursor-pointer hover:text-[var(--color-text-primary)]", "w-[130px]")} onClick={() => handleSort("status")}>
                  Status <SortIcon column="status" />
                </th>
                <th className={cn(thCls, "cursor-pointer hover:text-[var(--color-text-primary)]", "w-[100px]")} onClick={() => handleSort("source")}>
                  Source <SortIcon column="source" />
                </th>
                <th className={cn(thCls, "w-[110px]")}>Segment</th>
                <th className={cn(thCls, "min-w-[160px]")}>Purpose</th>
                <th className={cn(thCls, "min-w-[160px]")}>Notes</th>
                <th className={cn(thCls, "min-w-[140px]")}>Next Action</th>
                <th className={cn(thCls, "cursor-pointer hover:text-[var(--color-text-primary)]", "w-[110px]")} onClick={() => handleSort("dateAdded")}>
                  Date <SortIcon column="dateAdded" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((contact) => (
                <tr
                  key={contact._id}
                  className="group hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors duration-[120ms]"
                  onClick={() => setSelectedContactId(contact._id)}
                >
                  <td className={cn(tdCls, "font-medium whitespace-nowrap")}>{contact.name}</td>
                  <td className={cn(tdCls, "text-[var(--color-text-secondary)] text-[13px]")}>
                    {contact.email ? (
                      <a
                        href={`mailto:${contact.email}`}
                        className="hover:text-[var(--color-accent)] transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {contact.email}
                      </a>
                    ) : (
                      <span className="text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                  <td className={tdCls} onClick={(e) => e.stopPropagation()}>
                    <StatusSelect
                      value={contact.status}
                      onChange={(s) => updateContact(contact._id, { status: s })}
                    />
                  </td>
                  <td className={tdCls}>
                    <Badge variant={contact.source === "waitlist" ? "accent" : contact.source === "twitter" ? "info" : contact.source === "linkedin" ? "success" : "default"}>
                      {contact.source}
                    </Badge>
                  </td>
                  <td className={cn(tdCls, "text-[13px] text-[var(--color-text-muted)] capitalize")}>
                    {getContactSegment(contact).replace("_", " ")}
                  </td>
                  <td className={cn(tdCls, "text-[13px]", !contact.purpose && "text-[var(--color-text-muted)] italic")}>
                    {contact.purpose || "—"}
                  </td>
                  <td className={cn(tdCls, "text-[13px]", !contact.notes && "text-[var(--color-text-muted)] italic")}>
                    {contact.notes || "—"}
                  </td>
                  <td className={cn(tdCls, "text-[13px]", !parseNextAction(contact.notes) && "text-[var(--color-text-muted)] italic")}>
                    {parseNextAction(contact.notes) ?? "—"}
                  </td>
                  <td className={cn(tdCls, "text-[13px] text-[var(--color-text-secondary)] tabular-nums whitespace-nowrap")}>
                    {new Date(contact.dateAdded).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Contact Detail Sheet ─────────────────────────── */}
      <Sheet
        open={selectedContact !== null}
        onOpenChange={(open) => { if (!open) setSelectedContactId(null); }}
        title={selectedContact?.name ?? "Contact"}
      >
        {selectedContact && (
          <ContactDetailSheet
            contact={selectedContact}
            onUpdate={(updates) => updateContact(selectedContact._id, updates)}
            onClose={() => setSelectedContactId(null)}
          />
        )}
      </Sheet>
    </div>
  );
}

// ── Status Select (isolated, no row propagation) ────────────
function StatusSelect({ value, onChange }: { value: ContactStatus; onChange: (s: ContactStatus) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ContactStatus)}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "h-[24px] rounded-[5px] border-none px-[4px]",
        "text-[11px] font-medium cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
        value === "to_contact" && "bg-amber-500/10 text-amber-400",
        value === "contacted" && "bg-sky-500/10 text-sky-400",
        value === "connected" && "bg-emerald-500/10 text-emerald-400",
        value === "not_relevant" && "bg-white/[0.06] text-[var(--color-text-secondary)]",
      )}
    >
      <option value="to_contact">To Contact</option>
      <option value="contacted">Contacted</option>
      <option value="connected">Connected</option>
      <option value="not_relevant">Not Relevant</option>
    </select>
  );
}

// ── KPI Chip ────────────────────────────────────────────────
function KpiChip({
  label,
  value,
  icon,
  variant = "default",
}: {
  label: string;
  value: number;
  icon: typeof UserMultiple02Icon;
  variant?: "default" | "warning" | "info" | "success";
}) {
  const bgMap: Record<string, string> = {
    default: "bg-[var(--color-bg-sidebar)]",
    warning: "bg-amber-500/10",
    info: "bg-sky-500/10",
    success: "bg-emerald-500/10",
  };
  const textMap: Record<string, string> = {
    default: "text-[var(--color-text-primary)]",
    warning: "text-amber-400",
    info: "text-sky-400",
    success: "text-emerald-400",
  };

  return (
    <div className={cn(
      "flex items-center gap-[var(--spacing-2)] h-[36px] px-[var(--spacing-3)] rounded-[var(--radius-md)]",
      "border border-[var(--color-border-stroke)]",
      bgMap[variant],
    )}>
      <HugeiconsIcon icon={icon} size={16} className={textMap[variant]} />
      <span className={cn("text-[16px] font-semibold tabular-nums", textMap[variant])}>{value}</span>
      <span className="text-[12px] text-[var(--color-text-muted)]">{label}</span>
    </div>
  );
}

// ── Contact Detail Sheet Content ────────────────────────────
function ContactDetailSheet({
  contact,
  onUpdate,
  onClose,
}: {
  contact: ConvexContact;
  onUpdate: (updates: { status?: ContactStatus; purpose?: string; notes?: string; email?: string; platform?: string }) => void;
  onClose: () => void;
}) {
  const [purpose, setPurpose] = useState(contact.purpose);
  const [notes, setNotes] = useState(contact.notes);
  const [platform, setPlatform] = useState(contact.platform);
  const [email, setEmail] = useState(contact.email);

  const hasChanges =
    purpose !== contact.purpose ||
    notes !== contact.notes ||
    platform !== contact.platform ||
    email !== contact.email;

  const handleSave = () => {
    onUpdate({ purpose, notes, platform, email });
  };

  const fieldCls = cn(
    "w-full rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
    "bg-[var(--color-bg-canvas)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
    "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
    "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
    "transition-colors duration-[var(--duration-fast)]",
  );

  const labelCls = "text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-muted)] mb-[var(--spacing-1)]";

  const formattedDate = new Date(contact.dateAdded).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <>
      <SheetHeader onClose={onClose}>
        <div className="min-w-0">
          <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] truncate">
            {contact.name}
          </h2>
        </div>
      </SheetHeader>

      <SheetBody>
        <div className="flex flex-col gap-[var(--spacing-6)]">
          {/* ── Info row ────────────────────────────────────── */}
          <div className="flex items-center gap-[var(--spacing-3)] flex-wrap">
            <StatusSelect
              value={contact.status}
              onChange={(s) => onUpdate({ status: s })}
            />
            <Badge variant={contact.source === "waitlist" ? "accent" : contact.source === "twitter" ? "info" : contact.source === "linkedin" ? "success" : "default"}>
              {contact.source}
            </Badge>
            <span className="text-[13px] text-[var(--color-text-muted)] tabular-nums">{formattedDate}</span>
          </div>

          {/* ── Email ───────────────────────────────────────── */}
          <div>
            <label className={labelCls}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className={fieldCls}
            />
          </div>

          {/* ── Platform / Handle ────────────────────────────── */}
          <div>
            <label className={labelCls}>Platform / Handle</label>
            <input
              type="text"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              placeholder="@handle or linkedin.com/in/..."
              className={fieldCls}
            />
          </div>

          {/* ── Purpose ──────────────────────────────────────── */}
          <div>
            <label className={labelCls}>Purpose</label>
            <textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="What can this person help with? Partnerships, feedback, distribution…"
              rows={3}
              className={cn(fieldCls, "resize-y min-h-[72px]")}
            />
          </div>

          {/* ── Notes ────────────────────────────────────────── */}
          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Context, conversation history, next steps…"
              rows={4}
              className={cn(fieldCls, "resize-y min-h-[96px]")}
            />
          </div>

          {/* ── Save ─────────────────────────────────────────── */}
          {hasChanges && (
            <button
              onClick={handleSave}
              className={cn(
                "h-[36px] rounded-[var(--radius-md)] px-[var(--spacing-4)]",
                "bg-[var(--color-accent-500)] text-white text-[14px] font-medium",
                "hover:bg-[var(--color-accent-400)] active:scale-[0.98]",
                "transition-all duration-[var(--duration-fast)] cursor-pointer",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
              )}
            >
              Save changes
            </button>
          )}
        </div>
      </SheetBody>
    </>
  );
}
